import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatINR } from "@/lib/brand";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";

// Seven sophisticated LOOXS colour finishes for the gift card.
// Every finish is a considered pairing of base + accent + label ink.
const FINISHES = [
  { name: "Onyx",       base: "#0f0d0b", accent: "#c9a961", ink: "#f4ecd8", desc: "Deep black · brushed gold" },
  { name: "Champagne",  base: "#efe1bc", accent: "#8b6f3d", ink: "#3a2916", desc: "Warm champagne · antique gold" },
  { name: "Bordeaux",   base: "#4a1220", accent: "#d4a25a", ink: "#f4ecd8", desc: "Deep bordeaux · pale gold" },
  { name: "Forest",     base: "#20342a", accent: "#c9a961", ink: "#f4ecd8", desc: "Forest green · warm gold" },
  { name: "Midnight",   base: "#0a1a3a", accent: "#c9a961", ink: "#f4ecd8", desc: "Midnight navy · pale brass" },
  { name: "Camel",      base: "#b98a56", accent: "#f4ecd8", ink: "#3a2916", desc: "Warm camel · ivory" },
  { name: "Bone",       base: "#f4ecd8", accent: "#8b6f3d", ink: "#3a2916", desc: "Bone ivory · dark gold" },
];

export default function Gifts() {
  const [cards, setCards] = useState([]);
  const [finishIdx, setFinishIdx] = useState(0);
  const { addGiftCard } = useCart();

  useEffect(() => { api.get("/gift-cards").then(r => setCards(r.data)); }, []);
  const f = FINISHES[finishIdx];

  return (
    <div className="bg-ivory text-ink pt-32 pb-24">
      <PageMeta title="LOOXS Gift Cards · Seven Finishes" description="A LOOXS gift card is delivered as a digital certificate, redeemable across the LOOXS universe. Choose from seven sophisticated finishes — Onyx, Champagne, Bordeaux, Forest, Midnight, Camel and Bone." />
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-6">The Present</p>
          <h1 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight">Gift Cards</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed">
            A LOOXS gift card is delivered as a digital certificate — redeemable in boutique or against any pre-order across the LOOXS universe. Seven finishes, one house.
          </p>
        </div>

        {/* Finish selector */}
        <div className="flex flex-wrap gap-3 justify-center mb-12" data-testid="gift-finish-picker">
          {FINISHES.map((fin, i) => (
            <button
              key={fin.name}
              data-testid={`gift-finish-${fin.name.toLowerCase()}`}
              onClick={() => setFinishIdx(i)}
              className={`flex items-center gap-3 px-4 py-2 border transition-colors duration-300 ${
                finishIdx === i ? "border-goldDark bg-goldDark/5" : "border-ink/15 hover:border-ink/50"
              }`}
            >
              <span className="w-4 h-4 rounded-full border border-black/20" style={{ background: fin.base }} />
              <span className="text-[10px] tracking-widest-plus uppercase text-ink">{fin.name}</span>
            </button>
          ))}
        </div>
        <p className="text-center text-[10px] tracking-widest-plus uppercase text-ink/50 mb-16">{f.desc}</p>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-6">
          {cards.map((c, i) => (
            <div key={c.amount} data-testid={`gift-card-${c.amount}`} className="group animate-fade-up" style={{ animationDelay: `${i*80}ms` }}>
              <div className="aspect-[3/2] p-8 relative overflow-hidden hover-shift transition-colors duration-700"
                   style={{ background: f.base, color: f.ink }}>
                <div className="absolute inset-0 opacity-30" style={{
                  background: `radial-gradient(circle at 20% 20%, ${f.accent}, transparent 55%), radial-gradient(circle at 80% 80%, ${f.accent}, transparent 55%)`
                }} />
                <div className="absolute inset-4 border" style={{ borderColor: f.accent, opacity: 0.4 }} />
                <div className="grain opacity-15" />
                <div className="relative h-full flex flex-col justify-between">
                  <div className="flex items-start justify-between">
                    <div>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/brand/looxs-wordmark.png`} alt="LOOXS" style={{ height: 48, width: "auto", display: "block" }} draggable="false" />
                    </div>
                    <p className="text-[10px] tracking-widest-plus uppercase" style={{ color: f.accent }}>{f.name} Edition</p>
                  </div>
                  <div className="text-right">
                    <p
                      data-testid={`gift-amount-${c.amount}`}
                      className="leading-none"
                      style={{ color: f.ink, fontFamily: "'Times New Roman', Times, serif", fontSize: "3.75rem", fontWeight: 500, letterSpacing: "-0.02em" }}
                    >
                      {c.label}
                    </p>
                    <p className="text-[10px] tracking-widest-plus uppercase mt-2" style={{ color: f.ink, opacity: 0.65 }}>Redeemable across LOOXS</p>
                  </div>
                </div>
              </div>
              <button
                data-testid={`add-gift-${c.amount}`}
                onClick={() => { addGiftCard(c.amount, `${c.label} · ${f.name}`); toast.success(`${c.label} ${f.name} gift card added to bag`); }}
                className="mt-6 w-full bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-goldDark transition-colors duration-500">
                Add · {formatINR(c.amount)}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-24 max-w-3xl mx-auto text-center">
          <div className="rule-gold w-24 mx-auto mb-8" />
          <p className="text-[10px] tracking-widest-plus uppercase text-black/50 mb-3">Note</p>
          <p className="font-editorial-italic text-2xl md:text-3xl leading-relaxed text-ink/80">
            LOOXS gift cards are the only pieces of the house delivered instantly. Every garment is dispatched from the LOOXS atelier in India.
          </p>
        </div>
      </div>
    </div>
  );
}
